import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useDeleteStandingsConfiguration } from "~pages/StandingsConfigurations/StandingsConfigurationListPage/hooks/useDeleteStandingsConfiguration.ts";
import { useFetchStandingsConfigurations } from "~pages/StandingsConfigurations/StandingsConfigurationListPage/hooks/useFetchStandingsConfigurations.ts";
import { useGetStandingsConfigurationTableColumns } from "~pages/StandingsConfigurations/StandingsConfigurationListPage/hooks/useGetStandingsConfigurationTableColumns.tsx";
import type { StandingConfiguration } from "~types/standingConfiguration.ts";

const StandingsConfigurationListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [massEditMode, setMassEditMode] = useState(false);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.standingsConfigurations.listPage;
  const columns = useGetStandingsConfigurationTableColumns(massEditMode);

  const { resetPagination } = paginationConfig;

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameLocalContains: searchText,
    }),
    [paginationConfig.pageSize, paginationConfig.offset, searchText],
  );

  const { data, refetch, isPending } = useFetchStandingsConfigurations({ customParams: listParams });
  const { mutate: deleteFn, isPending: isDeletePending } = useDeleteStandingsConfiguration();

  const getNavigateUrl = (standingConfigurationId = "") =>
    `/${customerId}/admin/${sport}/${organizationId}/standings-configuration/manage/${standingConfigurationId}`;

  return (
    <DataTable<StandingConfiguration>
      handleRefresh={() => {
        void refetch();
      }}
      title={formatMessage({ id: "standings.configurations" })}
      columns={columns}
      rowActions={{
        base: {
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.standingConfigurationId)),
          },
          delete: {
            onClick: (row) => deleteFn(row.standingConfigurationId),
            loading: isDeletePending,
          },
        },
      }}
      rowKey={"standingConfigurationId"}
      rowNameKey={"nameLocal"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.standingsConfigurations`}
      isDataLoading={isPending}
      handleAdd={() => navigate(getNavigateUrl())}
      addButtonIntlKey={"standings.configuration"}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      permissionNames={[PermissionNames.STANDING_CONFIGURATION]}
      onMassEditModeChangedHandler={setMassEditMode}
      massEditMode={massEditMode}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default StandingsConfigurationListPage;
